import { Component, Input, Output, EventEmitter, ViewChild, NgZone, ChangeDetectorRef, ElementRef, OnDestroy } from '@angular/core';
import { BlogDetailPanelComponent } from '../blog-detail-panel/blog-detail-panel.component';
import { PipelineRun, Blog, ContentSourceType, BlogRevision } from '../../types';

@Component({
  standalone: false,
  selector: 'mkt-run-detail',
  template: `
    <div class="mkt-run-detail"
         [class.mkt-run-detail--resizing]="resizing"
         [style.grid-template-columns]="gridColumns">
      <!-- Left: Source Panel -->
      <aside class="mkt-run-detail__source"
             [class.mkt-run-detail__source--collapsed]="leftCollapsed">
        <button class="mkt-run-detail__collapse-btn"
                (click)="leftCollapsed = !leftCollapsed"
                [title]="leftCollapsed ? 'Expand source panel' : 'Collapse source panel'">
          <i class="fa-solid"
             [ngClass]="leftCollapsed ? 'fa-chevron-right' : 'fa-chevron-left'"></i>
        </button>
        <ng-container *ngIf="!leftCollapsed">
          <div class="mkt-run-detail__source-header">
            <div class="mkt-run-detail__source-icon">
              <i [class]="sourceTypeIcon"></i>
            </div>
            <div>
              <h3 class="mkt-run-detail__source-title">{{ run.contentSource.Title }}</h3>
              <div class="mkt-run-detail__source-meta">
                <span *ngIf="run.contentSource.Speaker"><i class="fa-solid fa-user"></i> {{ run.contentSource.Speaker }}</span>
                <span *ngIf="run.contentSource.SourceDate"><i class="fa-solid fa-calendar"></i> {{ run.contentSource.SourceDate | date }}</span>
              </div>
            </div>
          </div>
          <div class="mkt-run-detail__source-content" *ngIf="run.contentSource.Content">
            <h4 class="mkt-run-detail__source-section-title">
              Source Content
              <button class="mkt-run-detail__toggle-btn" (click)="sourceCollapsed = !sourceCollapsed">
                <i class="fa-solid" [ngClass]="sourceCollapsed ? 'fa-chevron-down' : 'fa-chevron-up'"></i>
              </button>
            </h4>
            <div class="mkt-run-detail__source-text" *ngIf="!sourceCollapsed">
              {{ run.contentSource.Content }}
            </div>
          </div>
        </ng-container>
      </aside>

      <!-- Center: Blog Cards by Status -->
      <main class="mkt-run-detail__center">
        <div *ngFor="let group of statusGroups">
          <div class="mkt-run-detail__status-group" *ngIf="getBlogsByStatus(group).length > 0">
            <h4 class="mkt-run-detail__group-header">
              <mkt-status-badge [status]="group"></mkt-status-badge>
              <span class="mkt-run-detail__group-count">{{ getBlogsByStatus(group).length }}</span>
            </h4>
            <div class="mkt-run-detail__card-list">
              <div *ngFor="let blog of getBlogsByStatus(group); let i = index"
                   class="mkt-run-detail__card-wrapper"
                   [style.animation-delay.ms]="i * 150">
                <mkt-content-card
                  [blog]="blog"
                  [selected]="blog.ID === selectedBlogId"
                  [showActions]="true"
                  [revisionCount]="getRevisionCount(blog.ID)"
                  [latestFeedback]="getLatestFeedback(blog.ID)"
                  (select)="selectBlog.emit($event)"
                  (statusChange)="statusChange.emit($event)"
                  (deleteBlog)="deleteBlog.emit($event)">
                </mkt-content-card>
              </div>
            </div>
          </div>
        </div>
      </main>

      <!-- Resize Handle -->
      <div class="mkt-run-detail__resize-handle"
           *ngIf="selectedBlog"
           (mousedown)="onResizeStart($event)">
        <div class="mkt-run-detail__resize-grip"></div>
      </div>

      <!-- Right: Blog Detail Panel -->
      <mkt-blog-detail-panel
        *ngIf="selectedBlog"
        [blog]="selectedBlog"
        [allRevisions]="allRevisions"
        (close)="closeBlogDetail.emit()"
        (statusChange)="statusChange.emit($event)"
        (deleteBlog)="deleteBlog.emit($event)"
        (blogSaved)="blogSaved.emit($event)"
        (revisionRequested)="revisionRequested.emit($event)"
        #blogDetailPanel
        class="mkt-run-detail__panel">
      </mkt-blog-detail-panel>
    </div>
  `,
  styles: [`
    :host {
      display: block;
      flex: 1;
      overflow: hidden;
    }

    .mkt-run-detail {
      display: grid;
      height: 100%;
      overflow: hidden;
      position: relative;
      transition: grid-template-columns 0.3s ease;
    }
    .mkt-run-detail--resizing {
      transition: none !important;
    }

    /* Source Panel */
    .mkt-run-detail__source {
      background: var(--mj-bg-surface);
      border-right: 1px solid var(--mj-border-default);
      padding: 16px;
      overflow-y: auto;
    }
    .mkt-run-detail__source--collapsed {
      padding: 8px 0;
      display: flex;
      flex-direction: column;
      align-items: center;
    }
    .mkt-run-detail__collapse-btn {
      background: none;
      border: none;
      cursor: pointer;
      color: var(--mj-text-muted);
      width: 32px;
      height: 32px;
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 6px;
      font-size: 0.8rem;
      transition: background 0.15s, color 0.15s;
      flex-shrink: 0;
    }
    .mkt-run-detail__collapse-btn:hover {
      background: var(--mj-bg-surface-hover);
      color: var(--mj-text-primary);
    }
    .mkt-run-detail__source-header {
      display: flex;
      gap: 10px;
      align-items: flex-start;
      margin-bottom: 16px;
    }
    .mkt-run-detail__source-icon {
      width: 36px;
      height: 36px;
      border-radius: 8px;
      background: var(--mj-status-info-bg);
      color: var(--mj-brand-primary);
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 1rem;
      flex-shrink: 0;
    }
    .mkt-run-detail__source-title {
      margin: 0 0 4px;
      font-size: 0.9rem;
      font-weight: 600;
      color: var(--mj-text-primary);
    }
    .mkt-run-detail__source-meta {
      display: flex;
      flex-direction: column;
      gap: 2px;
      font-size: 0.75rem;
      color: var(--mj-text-muted);
    }
    .mkt-run-detail__source-meta i {
      margin-right: 4px;
    }
    .mkt-run-detail__source-section-title {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin: 0 0 8px;
      font-size: 0.8rem;
      font-weight: 600;
      color: var(--mj-text-secondary);
    }
    .mkt-run-detail__toggle-btn {
      background: none;
      border: none;
      cursor: pointer;
      color: var(--mj-text-muted);
      padding: 2px 6px;
      font-size: 0.75rem;
    }
    .mkt-run-detail__source-text {
      font-size: 0.8rem;
      line-height: 1.5;
      color: var(--mj-text-secondary);
      white-space: pre-wrap;
    }

    /* Center Cards */
    .mkt-run-detail__center {
      padding: 16px;
      overflow-y: auto;
      min-width: 200px;
    }
    .mkt-run-detail__status-group {
      margin-bottom: 20px;
    }
    .mkt-run-detail__group-header {
      display: flex;
      align-items: center;
      gap: 8px;
      margin: 0 0 10px;
    }
    .mkt-run-detail__group-count {
      font-size: 0.75rem;
      color: var(--mj-text-disabled);
      font-weight: 500;
    }
    .mkt-run-detail__card-list {
      display: flex;
      flex-direction: column;
      gap: 8px;
    }
    .mkt-run-detail__card-wrapper {
      opacity: 0;
      animation: mktCardIn 0.3s ease forwards;
    }

    /* Resize Handle */
    .mkt-run-detail__resize-handle {
      cursor: col-resize;
      display: flex;
      align-items: center;
      justify-content: center;
      background: transparent;
      transition: background 0.15s;
    }
    .mkt-run-detail__resize-handle:hover {
      background: color-mix(in srgb, var(--mj-brand-primary) 8%, transparent);
    }
    .mkt-run-detail__resize-grip {
      width: 2px;
      height: 32px;
      border-radius: 1px;
      background: var(--mj-border-default);
      transition: background 0.15s;
    }
    .mkt-run-detail__resize-handle:hover .mkt-run-detail__resize-grip {
      background: var(--mj-brand-primary);
    }

    /* Side Panel */
    .mkt-run-detail__panel {
      border-left: 1px solid var(--mj-border-default);
      animation: mktSlideIn 0.25s ease forwards;
    }

    @keyframes mktCardIn {
      from { opacity: 0; transform: translateY(12px); }
      to { opacity: 1; transform: translateY(0); }
    }
    @keyframes mktSlideIn {
      from { transform: translateX(20px); opacity: 0; }
      to { transform: translateX(0); opacity: 1; }
    }
  `]
})
export class RunDetailComponent implements OnDestroy {
  @Input() run!: PipelineRun;
  @Input() blogs: Blog[] = [];
  @Input() contentSourceTypes: ContentSourceType[] = [];
  @Input() selectedBlogId: string | null = null;
  @Input() selectedBlog: Blog | null = null;
  @Input() allRevisions: BlogRevision[] = [];

  @Output() selectBlog = new EventEmitter<string>();
  @Output() closeBlogDetail = new EventEmitter<void>();
  @Output() statusChange = new EventEmitter<{ blogId: string; newStatus: string }>();
  @Output() deleteBlog = new EventEmitter<string>();
  @Output() blogSaved = new EventEmitter<string>();
  @Output() revisionRequested = new EventEmitter<{ blogId: string; feedback: string; feedbackOnly: boolean; critiqueType: string }>();

  @ViewChild('blogDetailPanel') blogDetailPanel?: BlogDetailPanelComponent;

  statusGroups: string[] = ['Draft', 'Ready', 'Published', 'Archived'];
  sourceCollapsed: boolean = false;

  // Left panel — collapsed by default so blog preview gets more space
  leftCollapsed = true;

  // Right panel resize — start wide since the preview is the primary focus
  rightPanelWidth = 520;
  resizing = false;
  private readonly RIGHT_MIN = 380;
  private readonly RIGHT_MAX_PCT = 0.65;
  private readonly LEFT_EXPANDED = 280;
  private readonly LEFT_EXPANDED_WITH_PANEL = 240;
  private readonly LEFT_COLLAPSED = 48;
  private startX = 0;
  private startWidth = 0;

  constructor(
    private ngZone: NgZone,
    private cdr: ChangeDetectorRef,
    private elementRef: ElementRef
  ) {}

  get gridColumns(): string {
    const left = this.leftCollapsed
      ? this.LEFT_COLLAPSED
      : (this.selectedBlogId ? this.LEFT_EXPANDED_WITH_PANEL : this.LEFT_EXPANDED);
    return this.selectedBlogId
      ? `${left}px 1fr 6px ${this.rightPanelWidth}px`
      : `${left}px 1fr`;
  }

  get sourceTypeIcon(): string {
    const typeName = this.contentSourceTypes.find(t => t.ID === this.run.contentSource.ContentSourceTypeID)?.Name ?? '';
    switch (typeName) {
      case 'Podcast Transcript': return 'fa-solid fa-podcast';
      case 'Webinar Recording': return 'fa-solid fa-video';
      case 'Document': return 'fa-solid fa-file-lines';
      default: return 'fa-solid fa-file';
    }
  }

  getBlogsByStatus(status: string): Blog[] {
    return this.blogs.filter(b => b.Status === status);
  }

  getRevisionCount(blogId: string): number {
    return this.allRevisions.filter(r => r.BlogID === blogId).length;
  }

  getLatestFeedback(blogId: string): string {
    const rev = this.allRevisions.find(r => r.BlogID === blogId && r.Feedback);
    return rev?.Feedback || '';
  }

  onResizeStart(e: MouseEvent) {
    e.preventDefault();
    this.resizing = true;
    this.startX = e.clientX;
    this.startWidth = this.rightPanelWidth;
    document.body.style.userSelect = 'none';
    document.body.style.cursor = 'col-resize';
    this.ngZone.runOutsideAngular(() => {
      document.addEventListener('mousemove', this.onResizeMove);
      document.addEventListener('mouseup', this.onResizeEnd);
    });
  }

  private onResizeMove = (e: MouseEvent) => {
    const delta = this.startX - e.clientX;
    const host = this.elementRef.nativeElement.offsetWidth;
    this.rightPanelWidth = Math.min(
      host * this.RIGHT_MAX_PCT,
      Math.max(this.RIGHT_MIN, this.startWidth + delta)
    );
    const grid = this.elementRef.nativeElement.querySelector('.mkt-run-detail') as HTMLElement;
    if (grid) {
      const left = this.leftCollapsed ? this.LEFT_COLLAPSED : this.LEFT_EXPANDED_WITH_PANEL;
      grid.style.gridTemplateColumns = `${left}px 1fr 6px ${this.rightPanelWidth}px`;
    }
  };

  private onResizeEnd = () => {
    this.resizing = false;
    document.body.style.userSelect = '';
    document.body.style.cursor = '';
    document.removeEventListener('mousemove', this.onResizeMove);
    document.removeEventListener('mouseup', this.onResizeEnd);
    this.ngZone.run(() => this.cdr.detectChanges());
  };

  ngOnDestroy() {
    document.removeEventListener('mousemove', this.onResizeMove);
    document.removeEventListener('mouseup', this.onResizeEnd);
  }
}
